import styles from './MainSigninSection.module.scss';
import { SSOButton } from 'src/v2/pages/auth/components/SSOButton/SSOButton';
import { AppRoutes, SSOProvider } from 'src/types';
import React, { useContext, useState, useEffect, useMemo } from 'react';
import AuthContext from 'src/contexts/AuthContext';
import { TermsSection } from 'src/v2/pages/auth/components/TermsSection/TermsSection';
import { TextWithLinkSection } from 'src/v2/pages/auth/components/TextWithLinkSection';
import { Link, useNavigate } from 'react-router-dom';
import { SN_UI_DOMAIN, UI_DOMAIN } from 'src/constants';
import { EmailSubmitForm } from 'src/v2/pages/auth/components/EmailSubmitForm';
import { getLastLogin } from 'src/utils/lastLoginStorage';
import { EMAIL_LOGIN_PROVIDER } from 'src/types';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { env } from 'src/env';

export const MainSigninSection = () => {
  const navigate = useNavigate();
  const { setAuthLoading } = useContext(AuthContext);

  const [showMore, setShowMore] = useState(false);
  const loginInfo = getLastLogin();

  const focusedProvider = useMemo(() => {
    if (loginInfo && loginInfo.loginType !== EMAIL_LOGIN_PROVIDER) {
      return loginInfo.loginType as SSOProvider;
    }
    return null;
  }, [loginInfo]);
  const isFacebookSigninDisabled = useFeatureFlagEnabled(
    'disable-facebook-signin',
  );
  const isCreditBasedPaymentEnabled = useFeatureFlagEnabled(
    'credit-based-system',
  );

  useEffect(() => {
    const providersToShowMore = [SSOProvider.LINKEDIN, SSOProvider.AMAZON];
    if (!isFacebookSigninDisabled) {
      providersToShowMore.push(SSOProvider.FACEBOOK);
    }
    if (
      loginInfo &&
      loginInfo.loginType !== EMAIL_LOGIN_PROVIDER &&
      providersToShowMore.includes(loginInfo.loginType)
    ) {
      setShowMore(true);
    }
  }, [loginInfo, isFacebookSigninDisabled]);

  const toggleShowMore = () => {
    setShowMore((prev) => !prev);
  };

  const handleEmailSubmit = (email: string) => {
    const encodedEmail = encodeURIComponent(email);
    const redirectToEmailSignUp = `${AppRoutes.LOGIN_WITH_EMAIL}?email=${encodedEmail}`;
    navigate(redirectToEmailSignUp);
  };

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.signInHeaderSection}>
          {isCreditBasedPaymentEnabled ? (
            <a
              href={env.REACT_APP_SUPER_NINJA_URL}
              className={styles.backToHome}
            >
              <span className={styles.backToHomeLink}>
                Back to {SN_UI_DOMAIN}
              </span>
            </a>
          ) : (
            <Link to={AppRoutes.HOME} className={styles.backToHome}>
              <span className={styles.backToHomeLink}>Back to {UI_DOMAIN}</span>
            </Link>
          )}
          <h2 className={styles.signInHeader}>Log in</h2>
        </div>
        <div className={styles.ssoSection}>
          <SSOButton
            ssoProvider={SSOProvider.GOOGLE}
            setAuthLoading={setAuthLoading}
            showFullButton={true}
            showLastUsedLabel={focusedProvider === SSOProvider.GOOGLE}
          />
          <SSOButton
            ssoProvider={SSOProvider.APPLE}
            setAuthLoading={setAuthLoading}
            showFullButton={true}
            showLastUsedLabel={focusedProvider === SSOProvider.APPLE}
          />
          <SSOButton
            ssoProvider={SSOProvider.MICROSOFT}
            setAuthLoading={setAuthLoading}
            showFullButton={true}
            showLastUsedLabel={focusedProvider === SSOProvider.MICROSOFT}
          />
          {showMore ? (
            <>
              {!isFacebookSigninDisabled && (
                <SSOButton
                  ssoProvider={SSOProvider.FACEBOOK}
                  setAuthLoading={setAuthLoading}
                  showFullButton={true}
                  showLastUsedLabel={focusedProvider === SSOProvider.FACEBOOK}
                />
              )}
              <SSOButton
                ssoProvider={SSOProvider.AMAZON}
                setAuthLoading={setAuthLoading}
                showFullButton={true}
                showLastUsedLabel={focusedProvider === SSOProvider.AMAZON}
              />
              <SSOButton
                ssoProvider={SSOProvider.LINKEDIN}
                setAuthLoading={setAuthLoading}
                showFullButton={true}
                showLastUsedLabel={focusedProvider === SSOProvider.LINKEDIN}
              />
            </>
          ) : (
            <div className={styles.showMoreSection}>
              {!isFacebookSigninDisabled && (
                <SSOButton
                  ssoProvider={SSOProvider.FACEBOOK}
                  setAuthLoading={setAuthLoading}
                  smallButton={true}
                  showLastUsedLabel={focusedProvider === SSOProvider.FACEBOOK}
                />
              )}
              <SSOButton
                ssoProvider={SSOProvider.AMAZON}
                setAuthLoading={setAuthLoading}
                smallButton={true}
                showLastUsedLabel={focusedProvider === SSOProvider.AMAZON}
              />
              <SSOButton
                ssoProvider={SSOProvider.LINKEDIN}
                setAuthLoading={setAuthLoading}
                smallButton={true}
                showLastUsedLabel={focusedProvider === SSOProvider.LINKEDIN}
              />
              <button
                className={styles.showMoreButton}
                onClick={toggleShowMore}
              >
                Show more
              </button>
            </div>
          )}
        </div>
        <div className={styles.ssoSectionSeparator}>
          <div className={styles.lineSeparator} />
          <div>OR</div>
          <div className={styles.lineSeparator} />
        </div>
        <EmailSubmitForm
          onSubmit={handleEmailSubmit}
          defaultEmail={
            loginInfo?.loginType === EMAIL_LOGIN_PROVIDER
              ? loginInfo?.email
              : undefined
          }
        />

        <TextWithLinkSection
          text="Don’t have an account?"
          linkText="Sign Up"
          linkRoute={AppRoutes.SIGN_UP}
        />
        <TermsSection />
      </div>
    </div>
  );
};
